// biome-ignore-all lint/a11y: TODO - address a11y issues later
import {
  ArrowLeftCircle,
  ArrowRightCircle,
  Calendar,
  ChevronsUpDown,
  ExternalLink,
  Users,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { CampaignHierarchy } from "@/components/campaign-hierarchy";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { SystemId } from "@/components/ui/system-id";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Activity } from "@/hooks/api/use-account-activities";
import { useActivityExternalConnectionLink } from "@/hooks/api/use-external-connection-links";
import { useTouchpointPersons } from "@/hooks/api/use-touchpoint-persons";
import { useTouchpointIdSidebarEnabled } from "@/hooks/use-feature-flags";
import { formatDate, formatFullDate } from "@/lib/date-formatting";
import { getExternalSystemName } from "@/lib/external-connection-links";
import type { CollapsedActivity } from "@/pages/accounts/account-explorer/touchpoints-table/types";

function formatDateRange(start: string, end: string) {
  return `${formatDate(start)} - ${formatDate(end)}`;
}

function GroupSummary({
  collapsedActivity,
}: {
  collapsedActivity: CollapsedActivity;
}) {
  // Get unique channels from all activities
  const uniqueChannels = Array.from(
    new Set(collapsedActivity.activities.map((activity) => activity.channel)),
  ).sort();

  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-xl font-bold">
        {collapsedActivity.activity || "Grouped Activities"}
      </h2>
      <div className="text-sm text-muted-foreground">
        <div className="flex items-center gap-2 flex-wrap">
          {uniqueChannels.map((channel) => (
            <Badge
              key={channel}
              variant="secondary"
              className="bg-accent hover:bg-accent text-accent-foreground"
            >
              {channel}
            </Badge>
          ))}
          <span>•</span>
          {collapsedActivity.activities.length} touchpoints
          <span>•</span>
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {formatDateRange(collapsedActivity.date, collapsedActivity.endDate)}
          </span>
        </div>
      </div>
    </div>
  );
}

function TouchpointParticipants({
  collapsedActivity,
  accountId,
  getPersonNameOverride,
  getPersonEmailOverride,
}: {
  collapsedActivity: CollapsedActivity;
  accountId?: string;
  getPersonNameOverride?: (personId: string) => string;
  getPersonEmailOverride?: (personId: string) => string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { getPersonName: getPersonNameFromApi, isEmployee } =
    useTouchpointPersons(
      getPersonNameOverride || getPersonEmailOverride ? undefined : accountId,
    );
  const getPersonName = getPersonNameOverride ?? getPersonNameFromApi;

  // Validate activities array and people property
  if (
    !collapsedActivity?.activities ||
    !Array.isArray(collapsedActivity.activities)
  ) {
    return null;
  }

  // Get unique people from all activities with defensive checks
  const uniquePeopleIds = new Set<string>();

  collapsedActivity.activities.forEach((activity) => {
    // Skip if people is not defined or not an array
    if (!activity.people || !Array.isArray(activity.people)) return;

    activity.people.forEach((person) => {
      // Skip invalid person objects
      if (!person || typeof person !== "object" || !person.id) return;
      uniquePeopleIds.add(person.id);
    });
  });

  // Convert to array of people objects
  const uniquePeople = Array.from(uniquePeopleIds).map((id) => ({
    name: getPersonName(id),
    id,
    isEmployee: isEmployee(id),
  }));

  if (uniquePeople.length === 0) return null;

  const displayPeople = isExpanded ? uniquePeople : uniquePeople.slice(0, 3);
  const hasMore = uniquePeople.length > 3;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Users className="h-4 w-4" />
        <span>{uniquePeople.length} People Involved</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {displayPeople.map(({ name, id }) => (
          <Tooltip key={id}>
            <TooltipTrigger asChild>
              <Badge
                variant="secondary"
                className="bg-accent hover:bg-accent text-accent-foreground"
              >
                {name}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p>Person ID: {id}</p>
            </TooltipContent>
          </Tooltip>
        ))}
        {!isExpanded && hasMore && (
          <button
            onClick={() => setIsExpanded(true)}
            className="text-sm text-muted-foreground hover:text-foreground shrink-0"
          >
            +{uniquePeople.length - 3} more
          </button>
        )}
      </div>
    </div>
  );
}

function TouchpointItem({
  activity,
  isExpanded,
  onToggle,
  showSeparator = true,
  isSelected,
  accountId,
  getPersonNameOverride,
  getPersonEmailOverride,
}: {
  activity: Activity;
  isExpanded: boolean;
  onToggle: () => void;
  showSeparator?: boolean;
  isSelected?: boolean;
  accountId?: string;
  getPersonNameOverride?: (personId: string) => string;
  getPersonEmailOverride?: (personId: string) => string;
}) {
  const { getPersonName: getPersonNameFromApi } = useTouchpointPersons(
    getPersonNameOverride || getPersonEmailOverride ? undefined : accountId,
  );
  const getPersonName = getPersonNameOverride ?? getPersonNameFromApi;
  const showTouchpointId = useTouchpointIdSidebarEnabled();
  const externalLink = useActivityExternalConnectionLink({
    accountId,
    activity,
  });
  const sourceRecord = getExternalSystemName(
    activity?.source_record_type || "",
  );

  // Format people names safely
  const personNames = useMemo(() => {
    if (!activity.people || !Array.isArray(activity.people)) {
      return "";
    }

    return activity.people
      .filter((person) => person && typeof person === "object" && person.id)
      .map((person) => getPersonName(person.id))
      .join(", ");
  }, [activity.people, getPersonName]);

  return (
    <>
      <div className="relative">
        {activity.direction && (
          <div className="absolute -left-6 top-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                {activity.direction === "IN" ? (
                  isSelected ? (
                    <div className="bg-primary rounded-full">
                      <ArrowRightCircle className="h-4 w-4 text-background" />
                    </div>
                  ) : (
                    <ArrowRightCircle className="h-4 w-4 text-primary" />
                  )
                ) : isSelected ? (
                  <div className="bg-muted-foreground/40 rounded-full">
                    <ArrowLeftCircle className="h-4 w-4 text-background" />
                  </div>
                ) : (
                  <ArrowLeftCircle className="h-4 w-4 text-muted-foreground/40" />
                )}
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {activity.direction === "IN" ? "Inbound" : "Outbound"}{" "}
                  activity
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        <div className="space-y-2 rounded-md">
          <div
            className="flex justify-between cursor-pointer rounded-sm px-2 py-1 -mx-2 hover:bg-muted/50"
            onClick={onToggle}
          >
            <div className="flex flex-col gap-1 flex-1">
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Users className="h-3 w-3" />
                {personNames}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge
                  variant="secondary"
                  className="bg-accent/30 hover:bg-accent/30 text-accent-foreground text-xs"
                >
                  {activity.channel}
                </Badge>
                {activity.status && (
                  <Badge className="text-xs border-muted-foreground/30">
                    {activity.status}
                  </Badge>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground ml-4 shrink-0">
              {formatFullDate(activity.date)}
            </p>
          </div>

          {isExpanded && (
            <div className="mt-2 text-sm whitespace-pre-wrap rounded-md py-2 px-2 bg-muted/30">
              <div className="space-y-4 relative">
                {externalLink && (
                  <div className="absolute top-0 right-0">
                    <a
                      href={externalLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:bg-muted rounded-md px-2 py-1 flex items-center gap-1 text-sm"
                    >
                      View in {sourceRecord}
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                )}
                {activity.team && (
                  <p>
                    <span className="text-muted-foreground">Team:</span>{" "}
                    {activity.team}
                  </p>
                )}

                {activity.campaign_id && (
                  <div>
                    <span className="text-muted-foreground">
                      Campaign Hierarchy:
                    </span>
                    <CampaignHierarchy campaignId={activity.campaign_id} />
                  </div>
                )}
                {showTouchpointId && (
                  <div className="flex justify-end">
                    <SystemId id={activity.id} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {showSeparator && <Separator className="my-4" />}
    </>
  );
}

interface TouchpointGroupProps {
  collapsedActivity: CollapsedActivity;
  accountId?: string;
  focusedActivityId?: string;
  allActivities: Activity[];
  getPersonNameOverride?: (personId: string) => string;
  getPersonEmailOverride?: (personId: string) => string;
}

export function TouchpointGroup({
  collapsedActivity,
  accountId,
  focusedActivityId,
  allActivities,
  getPersonNameOverride,
  getPersonEmailOverride,
}: TouchpointGroupProps) {
  const [selectedTouchpointId, setSelectedTouchpointId] = useState<
    string | undefined
  >(focusedActivityId);
  const [showCollapsed, setShowCollapsed] = useState(false);

  useEffect(() => {
    setSelectedTouchpointId(focusedActivityId);
  }, [focusedActivityId]);

  // Get visible activities based on connection logic
  const visibleActivities = useMemo(() => {
    const focusedActivity =
      collapsedActivity.activities.find((a) => a.id === focusedActivityId) ||
      collapsedActivity.activities[0];
    if (!focusedActivity) {
      return collapsedActivity.activities;
    }

    // Find all related activities using the same logic as use-thread-metadata
    const relatedActivities = allActivities.filter((activity) => {
      // Campaign connection
      if (
        focusedActivity.campaign_id &&
        activity.campaign_id === focusedActivity.campaign_id
      ) {
        return true;
      }
      // Activity description connection
      if (
        focusedActivity.activity &&
        activity.activity === focusedActivity.activity
      ) {
        return true;
      }

      return false;
    });

    return showCollapsed ? relatedActivities : relatedActivities.slice(0, 10);
  }, [
    allActivities,
    focusedActivityId,
    collapsedActivity.activities,
    showCollapsed,
  ]);

  // Calculate collapsed sections
  const getCollapsedInfo = useMemo(() => {
    if (visibleActivities.length < 10 || showCollapsed) {
      return { sections: [] };
    }

    return {
      sections: [
        {
          beforeIndex: 1,
          count: visibleActivities.length - 10,
        },
      ],
    };
  }, [visibleActivities.length, showCollapsed]);

  const toggleTouchpoint = (touchpointId: string) => {
    setSelectedTouchpointId((prev) =>
      prev === touchpointId ? undefined : touchpointId,
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col gap-6">
        <GroupSummary collapsedActivity={collapsedActivity} />
        <TouchpointParticipants
          collapsedActivity={collapsedActivity}
          accountId={accountId}
          getPersonNameOverride={getPersonNameOverride}
          getPersonEmailOverride={getPersonEmailOverride}
        />
      </div>

      <Separator className="my-6" />

      <div className="space-y-4 overflow-y-auto pr-4 flex-1">
        <div className="pl-6">
          {visibleActivities.map((activity, index) => {
            // Find if there should be a collapse indicator before this activity
            const collapsedSection = getCollapsedInfo.sections.find(
              (section) => section.beforeIndex === index,
            );

            // Find if there's a collapse indicator after this activity
            const hasCollapseAfter = getCollapsedInfo.sections.find(
              (section) => section.beforeIndex === index + 1,
            );

            return (
              <React.Fragment key={activity.id}>
                {collapsedSection && (
                  <div className="relative">
                    <div className="absolute left-0 right-0 top-[45%] h-[1px] bg-border" />
                    <div className="absolute left-0 right-0 top-[55%] h-[1px] bg-border" />

                    <button
                      onClick={() => setShowCollapsed(true)}
                      className="relative flex items-center gap-2 mx-3 group"
                    >
                      <div className="w-10 h-10 rounded-full bg-background border border-border flex items-center justify-center text-sm text-muted-foreground hover:text-accent-foreground group-hover:bg-accent relative">
                        <span className="group-hover:opacity-0 transition-opacity">
                          {collapsedSection.count}
                        </span>
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <ChevronsUpDown className="h-4 w-4" />
                        </div>
                      </div>
                    </button>
                  </div>
                )}
                <TouchpointItem
                  activity={activity}
                  isExpanded={activity.id === selectedTouchpointId}
                  onToggle={() => toggleTouchpoint(activity.id)}
                  showSeparator={
                    !hasCollapseAfter && index !== visibleActivities.length - 1
                  }
                  isSelected={activity.id === selectedTouchpointId}
                  accountId={accountId}
                  getPersonNameOverride={getPersonNameOverride}
                  getPersonEmailOverride={getPersonEmailOverride}
                />
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
